import React, { useContext, useEffect, useState, lazy, Suspense, useMemo, useRef } from 'react';

import Head from 'next/head';
import dynamic from 'next/dynamic';

import { PageElement } from '@hz-design-system/web-ui';

// Constants imports
import CATEGORIES_L1 from '@/constants/categories';
import VIEW_OPTIONS from '@/constants/viewOptions';
import { MIN_NUMBER_OF_LISTINGS_DDI } from '@/constants/seo';
import { SORT_BY } from '@/constants/sorting';

// Utils imports
import experiments from '@/utils/labs/experiments';
import getChannel from '@/client/utils/adsense/getChannel';
import { buildSmartLinkParameters, iosSmartBanner } from '@/utils/links/smartLinksHelpers';
import { PriceSuggestion } from '@/client/utils/seo/types';
import { getSearchCategoryName, getPageType, enrichLocaleAlt } from '@/client/utils/seoUtils';
import { adobeRegistration, sendEvent } from '@/client/utils/audience-targeting';
import getIsMobile, { getIsTabletAdaptingMobile } from '@/client/utils/browser/isMobile';
import getDominantCategory from '@/utils/getDominantCategory';
import isExcludedCategory from '@/utils/seo/isExcludedCategory';
import getRobotsContent from '@/client/utils/seo/robotsContent';
import trackEvent from '@/client/utils/analytics/trackEvent';
import stripHtml from '@/client/utils/seo/stripHtml';

// Helper imports
import paginationHelper from '@/common/helpers/paginationHelper';

// Action, context and hook imports
import actionStatus from '@/client/actions/actionStatus';
import storeSearches from '@/client/actions/storage/storeSearches';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import useCesSurvey from '@/client/hooks/useCesSurvey';

// Non-lazy imports of components
import { formatBreadcrumbs } from '@/client/components/Breadcrumb';
import SellerHeaderContainer from '@/client/components/molecules/SellerHeaderContainer';
import PageContainer from '@/client/components/Page/PageContainer';
import ZRPSearchHelper from '@/client/components/ZRPSearchHelper';
import SeoMetaTags from '@/client/components/Seo/SeoMetaTags';
import SSOIZeroResultsBox from '@/client/components/SSOIZeroResults/SSOIZeroResultsBox';
import NDFCGenericRankingsText from '@/client/components/NDFCGenericRankingsText/NDFCGenericRankingsText';
import Notification from '@/client/components/molecules/Notification';
import Banner from '@/client/components/Banner/Banner';
import CategorySuggestionTooltip from '@/client/components/CategorySuggestionTooltip/CategorySuggestionTooltip';
import AttributeSuggestionTooltip from '@/client/components/AttributeSuggestionTooltip/AttributeSuggestionTooltip';
import Listings from '@/client/components/Listings/Listings';
import FilterSidebar from '@/client/components/FilterSidebar';
import Breadcrumb from '@/client/components/Breadcrumb/Breadcrumb';
import FilterChips from '@/client/components/FilterChips';
import ViewControls from '@/client/components/ViewControls';
import { TRelevantInlink } from '@ecg-marktplaats/express-react/dist/types';

const ShippingBanner = dynamic(() => import('@/client/components/ShippingBanner/ShippingBanner'), {
  ssr: false,
});

// Lazy imports of components
const AdsenseContainer = lazy(() => import('@/client/components/AdsenseContainer/AdsenseContainer.tsx'));
const FaqFooter = lazy(() => import('@/client/components/FaqFooter/FaqFooter.tsx'));
const SuggestedSearchesWrapper = lazy(
  () => import('@/client/components/SuggestedSearches/SuggestedSearchesWrapper.tsx'),
);
const DataDrivenContent = lazy(() => import('@/client/components/Seo/DataDrivenContent.tsx'));
const PaginationControls = lazy(
  () => import('@/client/components/PaginationControls/organisms/PaginationControls.tsx'),
);
const BottomBlockLazyListings = lazy(() => import('@/client/components/Listings/BottomBlockLazyListings.tsx'));
const AndroidSmartBanner = lazy(() => import('@/client/components/LinkGenerator/AndroidSmartBanner.tsx'));
const HubPages = lazy(() => import('@/client/components/HubPages/HubPages.tsx'));

interface LRPContainerProps {
  meta: TSeoMetaTags;
  hrefLangUrl: string;
  requestStatus: string;
  searchRequestAndResponse: TSearchRequestAndResponse;
  seller: TSeller;
  headerProps: {
    searchQuery?: string;
    loginTargetUrl?: string;
    [key: string]: any;
  };
  paaContent?: { questions: Record<string, string>; answers: Record<string, string> };
  categories?: TCategories;
  hubPage?: string;
  relevantInlinks?: TRelevantInlink[];
  priceSuggestions: PriceSuggestion[];
  bannersConfig: TBannerServerData;
}

const TOP_BANNER_SLOT = 'banner-top-dt';

const LrpContainer: React.FC<LRPContainerProps> = ({
  meta,
  hrefLangUrl,
  requestStatus,
  searchRequestAndResponse,
  seller,
  headerProps,
  paaContent,
  hubPage,
  relevantInlinks,
  priceSuggestions,
  bannersConfig,
}) => {
  const {
    xsrfToken,
    tenantContext,
    audienceTargeting,
    userDetails,
    properties,
    labsConfig,
    isBot,
    seoProperties,
    showShippingCampaignBanner,
  } = useContext(EnvironmentContext);

  const { gclidQueryParam, androidPackage, androidScheme, iosAppId, iosProtocol } = properties || {};

  const {
    isSearchSaved = false,
    attributeHierarchy,
    categoriesById,
    originalQuery,
    searchRequest,
    searchCategory,
    facets,
    totalResultCount,
    hasErrors,
    metaTags,
    sortOptions,
    listings,
    topBlock,
    correlationId,
    correctedQuery,
    seoFriendlyAttributes,
    seoFriendlyTextAttributes,
    alternativeLocales,
    showSuggestionTooltip,
    showAttributeTooltip,
    suggestions,
  } = searchRequestAndResponse as TSearchRequestAndResponse;

  const { localeAlt, searchDistanceOptions, sharedDomainCookie } = headerProps;

  const [notification, setNotification] = useState<null | any>(null);
  const [isMobileFilterDialogOpen, setIsMobileFilterDialogOpen] = useState<boolean>(false);
  const [searchIsSaved, setSearchIsSaved] = useState<boolean>(isSearchSaved);
  const [savedSearchId, setSavedSearchId] = useState<number | null>(null);
  const [topBannerLoadingState, setTopBannerLoadingState] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const [bannersInitialized, setBannersInitialized] = useState<boolean>(false);
  const [showSmartBanner, setShowSmartBanner] = useState<boolean>(false);
  const [isWide, setIsWide] = useState<boolean>(false);
  const { loadSurvey } = useCesSurvey();
  const experimentsConfig = experiments({ labsConfig });
  const postponeBannersUntilClientSideSearch = useRef(false);
  const isMarktplaats = tenantContext.tenant === 'mp';
  const {
    isFetchResultsOnceEnabled,
    isFetchResultsOnceAllCatEnabled,
    isFetchResultsHIOnceEnabled,
    isCategorySuggestionTooltipEnabled,
    isAttributeSuggestionTooltipEnabled,
  } = experimentsConfig;
  const withAllAttributes =
    isFetchResultsOnceAllCatEnabled ||
    (isFetchResultsOnceEnabled && searchRequest.categories?.l1Category?.id === CATEGORIES_L1.DAMES_KLEREN) ||
    (isFetchResultsHIOnceEnabled &&
      isMarktplaats &&
      searchRequest.categories?.l1Category?.id === CATEGORIES_L1.HUIS_EN_INRICHTING);
  const isCarCategory = Boolean(categoriesById[CATEGORIES_L1.CARS]);
  const isRequestCompleted = [actionStatus.failure, actionStatus.success].includes(requestStatus);
  const isRequestSuccess = requestStatus === actionStatus.success;
  const isTenOrMoreListings = totalResultCount && totalResultCount >= MIN_NUMBER_OF_LISTINGS_DDI;
  const shouldShowHubpageContent = !seller && !!hubPage;

  const isSSOI = userDetails.isLoggedIn && seller && userDetails.id === seller.id;

  const dominantCategory = getDominantCategory(facets);

  const initBanners = (bannerConfig) => {
    if (!window.googletag) {
      window.googletag = { cmd: [] };
    } else if (!window.googletag.cmd) {
      window.googletag.cmd = [];
    }

    const bannerLibConfig = window?.mpBannerLibConfig?.mainScriptUrl ? window?.mpBannerLibConfig : bannerConfig;

    const bannerScript = document.createElement('script');
    bannerScript.setAttribute('data-testid', 'banner-script');
    bannerScript.setAttribute('src', bannerLibConfig?.mainScriptUrl);
    document.body.appendChild(bannerScript);

    window.googletag.cmd.push(() => {
      window.googletag.pubads().addEventListener('slotRenderEnded', function (event) {
        if (event.slot.getSlotElementId() === TOP_BANNER_SLOT) {
          setTopBannerLoadingState(false);
        }
      });
    });
  };

  const initAudienceTargeting = () => {
    adobeRegistration({ audienceTargeting, user: userDetails, tenantContext });
  };

  const trackAudienceTargetingEvent = () => {
    if (!audienceTargeting) return;
    sendEvent({
      audienceTargeting,
      user: userDetails,
      tenantContext,
      searchRequestAndResponse,
      xsrfToken,
    });
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(min-width: 1200px)');
      setIsWide(mediaQuery.matches);

      const handleResize = () => setIsWide(mediaQuery.matches);

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleResize);
        return () => mediaQuery.removeEventListener('change', handleResize);
      } else {
        // Legacy support
        mediaQuery.addListener(handleResize);
        return () => mediaQuery.removeListener(handleResize);
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile());

    const init = async (createdBannerConfig) => {
      postponeBannersUntilClientSideSearch.current = Boolean(window.location.hash);
      await initBanners(createdBannerConfig);
      initAudienceTargeting();
      setIsMobile(getIsMobile());
      window.addEventListener('resize', handleResize);
    };

    const resetBanners = async () => {
      if (!bannersInitialized) {
        await init(bannersConfig);
        setBannersInitialized(true);
      } else {
        window.ecgJsBanners?.resetAllBannersInPage();
      }

      if (!audienceTargeting) return;
      trackAudienceTargetingEvent();
    };

    const resetBannersIfNeeded = async () => {
      if (!postponeBannersUntilClientSideSearch.current) {
        await resetBanners();
      }
      postponeBannersUntilClientSideSearch.current = false;
    };

    resetBannersIfNeeded();
    (window as any).cssVars?.();
    return () => {
      window.removeEventListener('resize', handleResize);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRequest]);

  useEffect(() => {
    if ([actionStatus.failure, actionStatus.success].includes(requestStatus)) {
      setNotification(null);
    } else {
      setTopBannerLoadingState(true);
    }
  }, [requestStatus]);

  useEffect(() => {
    if (isCarCategory && userDetails.isLoggedIn && !seller && isRequestSuccess) {
      loadSurvey();
    }
  }, [isCarCategory, isRequestSuccess, loadSurvey, seller, userDetails.isLoggedIn]);

  useEffect(() => {
    if (searchRequest.categories?.l1Category && searchRequestAndResponse.facets) {
      storeSearches(searchRequest, searchRequestAndResponse.facets);
    }
  }, [searchRequest, searchRequest.categories?.l1Category, searchRequestAndResponse]);

  useEffect(() => {
    setSearchIsSaved(isSearchSaved);
  }, [isSearchSaved, searchRequest]);

  useEffect(() => {
    setIsMobileView(getIsTabletAdaptingMobile());
  }, []);

  const showMobileFilterDialog = () => {
    if (!isMobileView) return;

    setIsMobileFilterDialogOpen(true);
    trackEvent({ eventAction: 'LrpFiltersMobile', eventLabel: 'open' });
  };

  const hideMobileFilterDialog = () => {
    if (!isMobileView) return;

    setIsMobileFilterDialogOpen(false);
    trackEvent({ eventAction: 'LrpFiltersMobile', eventLabel: 'close' });
  };

  const pushNotification = (type: string, message: React.ReactElement) => setNotification({ type, message });

  const androidLinks = { androidPackage, androidScheme };
  const hasResults = isRequestSuccess && Boolean(totalResultCount);
  const isZSRP = isRequestSuccess && totalResultCount === 0;

  const searchRequestObject = useMemo((): TSearchRequestObject => {
    return {
      ...searchRequest,
      attributes: attributeHierarchy,
      seller,
      seoFriendlyAttributes,
      seoFriendlyTextAttributes,
    };
  }, [searchRequest, attributeHierarchy, seller, seoFriendlyAttributes, seoFriendlyTextAttributes]);

  const searchCategoryName = getSearchCategoryName(categoriesById, searchCategory);

  const pageType = getPageType(hasResults);

  const altLocaleWithUrl = enrichLocaleAlt({
    localeAlt,
    alternativeLocales,
    attributeHierarchy,
    searchRequestObject,
  });

  const isSOIPage = !!seller;
  const canShowBannersRef = useRef<boolean>(true);

  useEffect(() => {
    canShowBannersRef.current = isSOIPage ? seller.showBanners : true;

    setShowSmartBanner(Boolean(seller));
  }, [isSOIPage, seller]);

  const canShowBanners = canShowBannersRef.current;

  const breadcrumbs = useMemo(
    () => formatBreadcrumbs(searchRequestObject, categoriesById, seller, totalResultCount),
    [searchRequestObject, categoriesById, seller, totalResultCount],
  );

  const { currentPage } = useMemo(
    () => paginationHelper(searchRequest.pagination, totalResultCount),
    [searchRequest.pagination, totalResultCount],
  );

  const channelData = useMemo(
    () =>
      getChannel({
        isZrp: isZSRP,
        isSrp: !!originalQuery,
        labsConfig,
        categories: searchRequest.categories,
        view: searchRequest.viewOptions.kind,
        currentPage,
        facets,
      }),
    [isZSRP, originalQuery, labsConfig, searchRequest.categories, searchRequest.viewOptions.kind, currentPage, facets],
  );

  const { attributesById, attributesByKey, attributeRanges, categories } = searchRequest;
  const hasAttributes = !attributesById.length && !attributesByKey.length && !attributeRanges.length;
  const smartLinkParams = useMemo(
    () =>
      buildSmartLinkParameters({
        categories: searchRequest.categories,
        query: searchRequest.searchQuery,
        attributesValuesIds: searchRequest.attributes,
      }),
    [searchRequest.categories, searchRequest.searchQuery, searchRequest.attributes],
  );

  const iosLinks = { iosAppId, iosProtocol };
  const hasCategories = !!categories.l1Category || !!categories.l2Category;
  const isSOISearchWithNoSearchFilters = !hasCategories && !hasAttributes;
  const isSSOIWithZeroResults = isSSOI && isSOISearchWithNoSearchFilters && !hasResults;
  const currentSortBy = searchRequest.sortOptions?.sortBy;
  const customSortingApplied = !!currentSortBy && currentSortBy !== SORT_BY.OPTIMIZED;
  const showNDFCRankingsText = !isBot && !isZSRP && !customSortingApplied && !seller;
  const isBusinessSeller = seller?.active;
  const showBottomBlockListings =
    !seller && hasResults && !(isMobile && searchRequest?.viewOptions?.kind === VIEW_OPTIONS.GALLERY_VIEW);
  const shouldShowSrpDataDrivenContent =
    seoProperties?.dataDrivenContent?.isEnabledForTenant &&
    isTenOrMoreListings &&
    !isExcludedCategory(categories.l2Category?.id || categories.l1Category?.id || dominantCategory?.id);
  const shouldShowSellerDataDrivenContent = Boolean(
    !!seller &&
    isBusinessSeller &&
    seoProperties?.sellerDataDrivenContent?.isEnabledForTenant &&
    isTenOrMoreListings &&
    !isExcludedCategory(categories.l2Category?.id || categories.l1Category?.id || dominantCategory?.id),
  );

  const strippedH1Title = () => {
    const { pageTitleH1 } = metaTags;

    if (pageTitleH1.includes('<h1>')) {
      const h1Text = pageTitleH1.match(/<h1>(.*?)<\/h1>/)?.[1] || '';
      return stripHtml(h1Text);
    } else {
      return pageTitleH1;
    }
  };

  return (
    <>
      <Head>
        {showSmartBanner && <meta name="apple-itunes-app" content={iosSmartBanner(smartLinkParams, iosLinks)} />}
      </Head>
      <SeoMetaTags
        breadcrumbs={meta.breadcrumbs}
        product={meta.product}
        imageObject={meta.imageObject}
        canonicalUrl={meta.canonicalUrl}
        speakable={meta.speakable}
        title={metaTags?.metaTitle}
        description={metaTags.metaDescription}
        content={getRobotsContent(searchRequestObject, hasResults, withAllAttributes)}
        altLocaleWithUrl={altLocaleWithUrl}
        hrefLangUrl={hrefLangUrl}
        localeAlt={localeAlt}
      />
      <Suspense fallback>
        {showSmartBanner && <AndroidSmartBanner parameters={smartLinkParams} smartLinkProps={androidLinks} />}
      </Suspense>
      <PageContainer>
        {!seller && (
          <div className="speakable-content-hidden" hidden>
            <p className="title">{metaTags.metaTitle}</p>
            <p className="description">{metaTags.metaDescription}</p>
          </div>
        )}
        {seller && <SellerHeaderContainer sellerProfile={seller} pushNotification={pushNotification} />}
        {!isWide && canShowBanners && (
          <Banner position="top-dt" mobileOnly={true} isSOI={isSOIPage} type="fluid" loading={topBannerLoadingState} />
        )}
        <Notification notification={notification} requestStatus={requestStatus} />
        <PageElement className="hz-Page-element hz-Page-element--full-width hz-Page-element--breadCrumbAndSaveSearch">
          <Breadcrumb
            breadcrumbs={breadcrumbs}
            correctedQuery={correctedQuery}
            originalQuery={originalQuery}
            searchRequestObject={searchRequestObject}
          />
        </PageElement>
        <PageElement className={`hz-Page-element--aside${isCarCategory ? ' with-expandable-layout' : ''}`}>
          <FilterSidebar
            facets={facets}
            isMobile={isMobileView}
            isMobileFilterDialogOpen={isMobileFilterDialogOpen}
            onHideMobileFilterDialog={hideMobileFilterDialog}
            searchRequestObject={searchRequestObject}
            searchDistanceOptions={searchDistanceOptions}
            sharedDomainCookie={sharedDomainCookie}
            totalResults={totalResultCount}
            carsFilters={isCarCategory}
          />
          {!seller && <Banner position="skyscraper-dt" type="sticky-left" />}
          {!seller && hasResults && <Banner position="skyscraper-2-dt" type="sticky-left" />}
        </PageElement>
        <PageElement className="hz-Page-element--main">
          {!!isWide && canShowBanners && isZSRP && <Banner position="top-dt" mobileOnly={false} type="fluid" />}
          <>
            <ViewControls
              sortOptions={sortOptions}
              searchRequestObject={searchRequestObject}
              isSearchSaved={searchIsSaved}
              setIsSearchSaved={setSearchIsSaved}
              setSavedSearchId={setSavedSearchId}
              savedSearchId={savedSearchId}
              isSoiPage={!!seller}
            />
            <FilterChips
              searchRequestObject={searchRequestObject}
              attributeHierarchy={attributeHierarchy}
              categoriesById={categoriesById}
              facets={facets}
              showMobileFilterDialog={showMobileFilterDialog}
              isMobile={isMobileView}
            />
          </>
          {showSuggestionTooltip && (isCategorySuggestionTooltipEnabled || isAttributeSuggestionTooltipEnabled) && (
            <CategorySuggestionTooltip
              suggestedCategories={suggestions?.categories || []}
              searchRequestObject={searchRequestObject}
              isSticky={true}
              showHighlight={true}
            />
          )}
          {showAttributeTooltip && (
            <AttributeSuggestionTooltip
              suggestedAttributes={suggestions?.attributes?.entities || []}
              searchRequestObject={searchRequestObject}
              isCarCategory={isCarCategory}
              categoriesById={categoriesById}
              facets={facets}
              isSticky={true}
            />
          )}
          {!isCarCategory && showShippingCampaignBanner && (
            <Suspense fallback>
              <ShippingBanner />
            </Suspense>
          )}
          {showNDFCRankingsText && <NDFCGenericRankingsText sharedDomainCookie={sharedDomainCookie} />}
          {!isZSRP && (
            <Listings
              topBlock={topBlock}
              listings={listings}
              searchRequestObject={searchRequestObject}
              correlationId={correlationId}
              showTopBanner={isWide && canShowBanners}
              showMidBanner={!isSOIPage}
              showAdsenseMidBanner={!isSOIPage}
              isSOIPage={isSOIPage}
              topBannerLoadingState={topBannerLoadingState}
              searchCategoryName={searchCategoryName}
              pageType={pageType}
              currentPage={currentPage}
              channel={channelData}
            />
          )}

          {showBottomBlockListings && <BottomBlockLazyListings searchRequestObject={searchRequest} />}
          {isZSRP && (
            <>
              {isSSOIWithZeroResults && <SSOIZeroResultsBox />}
              {!isSSOIWithZeroResults && (
                <ZRPSearchHelper
                  searchRequestObject={searchRequestObject}
                  hasErrors={hasErrors}
                  suggestions={suggestions}
                />
              )}
            </>
          )}
          {!seller && isRequestCompleted && !gclidQueryParam && (
            <AdsenseContainer
              query={searchRequest && searchRequest.searchQuery}
              searchCategoryName={searchCategoryName}
              pageType={pageType}
              currentPage={currentPage}
              channel={channelData}
            />
          )}
          {hasResults && (
            <Suspense fallback>
              <PaginationControls
                isNewPagination
                {...paginationHelper(searchRequest.pagination, totalResultCount)}
                searchRequestObject={searchRequestObject}
                showPageSizeControls={false}
              />
            </Suspense>
          )}
          {metaTags?.pageTitleH1 && (
            <Suspense fallback>
              <div className="hz-H1Title">
                {/* eslint-disable-next-line react/no-danger */}
                <h1 dangerouslySetInnerHTML={{ __html: metaTags?.pageTitleH1 ? strippedH1Title() : '' }} />
              </div>
            </Suspense>
          )}
          {!seller && !!isWide && hasResults && (
            <Suspense fallback>
              <SuggestedSearchesWrapper relevantInlinks={relevantInlinks} />
            </Suspense>
          )}
          {!seller && !!isWide && isRequestCompleted && <Banner position="bottom-dt-l" type="fluid" />}
        </PageElement>
        {!seller && !isWide && hasResults && (
          <PageElement kind="full-width">
            <Suspense fallback>
              <SuggestedSearchesWrapper relevantInlinks={relevantInlinks} />
            </Suspense>
          </PageElement>
        )}
        {!seller && !isWide && isRequestCompleted && <Banner position="bottom-dt-l" type="fluid" />}
        {(shouldShowSrpDataDrivenContent || shouldShowSellerDataDrivenContent) && (
          <Suspense fallback>
            <DataDrivenContent
              originalQuery={originalQuery}
              facets={facets}
              categories={searchRequest.categories}
              totalResultCount={totalResultCount}
              priceSuggestions={priceSuggestions}
              dominantCategory={dominantCategory}
              dataTestId={shouldShowSellerDataDrivenContent ? 'SellerDataDrivenContent' : 'DataDrivenContent'}
              {...(shouldShowSellerDataDrivenContent && { seller })}
            />
          </Suspense>
        )}
        {shouldShowHubpageContent && (
          <Suspense fallback>
            <HubPages content={hubPage || ''} />
          </Suspense>
        )}
        {!seller && (
          <Suspense fallback>
            <FaqFooter seoFaqData={paaContent} searchRequestObject={searchRequestObject} />
          </Suspense>
        )}

        <PageElement className="hz-Page-element hz-Page-element--full-width hz-Page-element--bottomBreadCrumbAndSaveSearch">
          <Breadcrumb
            breadcrumbs={breadcrumbs}
            correctedQuery={correctedQuery}
            originalQuery={originalQuery}
            searchRequestObject={searchRequestObject}
          />
        </PageElement>
      </PageContainer>
    </>
  );
};

export default LrpContainer;
